/**
 * @file Card.jsx
 * @description Composable card component.
 *
 * @param {'elevated'|'outline'|'flat'|'interactive'} [variant='elevated']
 * @param {'none'|'sm'|'md'|'lg'} [padding='md']
 * @param {boolean} [clickable=false] - Makes the entire card a clickable target
 * @param {boolean} [selected=false] - Shows selected state (blue border)
 * @param {boolean} [loading=false] - Shows skeleton shimmer overlay
 * @param {string} [media] - Image URL for top media slot
 * @param {string} [mediaAlt] - Alt text for media image
 * @param {Function} [onClick]
 * @param {string} [href] - If set, renders as <a>
 * @param {string} [className]
 */
import React, { useState } from 'react';

const paddingMap = {
  none: '0',
  sm: 'var(--space-3)',
  md: 'var(--space-5)',
  lg: 'var(--space-6)',
};

const variantStyles = {
  elevated: {
    backgroundColor: 'var(--surface-raised)',
    border: '1px solid var(--border-default)',
    boxShadow: 'var(--shadow-sm)',
  },
  outline: {
    backgroundColor: 'var(--surface-raised)',
    border: '1px solid var(--border-default)',
    boxShadow: 'none',
  },
  flat: {
    backgroundColor: 'var(--surface-sunken)',
    border: '1px solid transparent',
    boxShadow: 'none',
  },
  interactive: {
    backgroundColor: 'var(--surface-raised)',
    border: '1px solid var(--border-default)',
    boxShadow: 'var(--shadow-xs)',
    cursor: 'pointer',
  },
};

/* Inject shimmer keyframes once */
let shimmerInjected = false;
function injectShimmer() {
  if (shimmerInjected || typeof document === 'undefined') return;
  const style = document.createElement('style');
  style.textContent = `
@keyframes ib-card-shimmer {
  0% { transform: translateX(-100%); }
  100% { transform: translateX(100%); }
}`;
  document.head.appendChild(style);
  shimmerInjected = true;
}

function Card({
  variant = 'elevated',
  padding: paddingProp = 'md',
  clickable = false,
  selected = false,
  loading = false,
  media,
  mediaAlt = '',
  onClick,
  href,
  className = '',
  children,
  style: userStyle,
  ...rest
}) {
  const [hovered, setHovered] = useState(false);

  React.useEffect(() => { injectShimmer(); }, []);

  const isClickable = clickable || Boolean(onClick) || Boolean(href) || variant === 'interactive';

  const computedStyle = {
    position: 'relative',
    borderRadius: 'var(--radius-xl)',
    overflow: 'hidden',
    transition: `all var(--transition-fast) var(--ease-default)`,
    textDecoration: 'none',
    color: 'inherit',
    display: 'block',
    ...variantStyles[variant],
    ...(selected ? {
      borderColor: 'var(--color-primary)',
      boxShadow: '0 0 0 2px var(--color-primary)',
    } : {}),
    ...(isClickable && hovered && !selected ? {
      boxShadow: 'var(--shadow-md)',
      borderColor: 'var(--border-strong)',
      transform: 'translateY(-1px)',
    } : {}),
    ...userStyle,
  };

  const Component = href ? 'a' : isClickable ? 'button' : 'div';

  const componentProps = {
    className: `ib-card ib-card--${variant} ${className}`.trim(),
    style: computedStyle,
    onClick: isClickable ? onClick : undefined,
    href: href || undefined,
    onMouseEnter: isClickable ? () => setHovered(true) : undefined,
    onMouseLeave: isClickable ? () => setHovered(false) : undefined,
    role: isClickable && !href ? 'button' : undefined,
    tabIndex: isClickable ? 0 : undefined,
    'aria-selected': selected || undefined,
    ...(Component === 'button' ? {
      type: 'button',
      style: { ...computedStyle, width: '100%', textAlign: 'left', fontFamily: 'inherit', fontSize: 'inherit', background: computedStyle.backgroundColor, cursor: 'pointer', border: computedStyle.border },
    } : {}),
    ...rest,
  };

  const paddingValue = paddingMap[paddingProp] || paddingMap.md;

  return (
    <Component {...componentProps}>
      {media && (
        <div style={{ width: '100%', overflow: 'hidden', backgroundColor: 'var(--surface-sunken)' }}>
          <img
            src={media}
            alt={mediaAlt}
            style={{ width: '100%', height: 'auto', display: 'block', objectFit: 'cover' }}
            loading="lazy"
          />
        </div>
      )}
      <div style={{ padding: paddingValue }}>
        {children}
      </div>

      {loading && (
        <div style={{
          position: 'absolute',
          inset: 0,
          backgroundColor: 'var(--surface-raised)',
          opacity: 0.85,
          overflow: 'hidden',
        }}>
          <div style={{
            position: 'absolute',
            inset: 0,
            background: 'linear-gradient(90deg, transparent, var(--overlay-light), transparent)',
            animation: 'ib-card-shimmer 1.5s ease-in-out infinite',
          }} />
        </div>
      )}
    </Component>
  );
}

/** Card.Header — top section with title/actions */
function CardHeader({ children, style: userStyle, ...rest }) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: 'var(--space-3)',
        ...userStyle,
      }}
      {...rest}
    >
      {children}
    </div>
  );
}

/** Card.Body — main content area */
function CardBody({ children, style: userStyle, ...rest }) {
  return (
    <div style={{ ...userStyle }} {...rest}>
      {children}
    </div>
  );
}

/** Card.Footer — bottom section, usually actions */
function CardFooter({ children, style: userStyle, ...rest }) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-end',
        gap: 'var(--space-2)',
        marginTop: 'var(--space-4)',
        paddingTop: 'var(--space-3)',
        borderTop: '1px solid var(--border-default)',
        ...userStyle,
      }}
      {...rest}
    >
      {children}
    </div>
  );
}

Card.Header = CardHeader;
Card.Body = CardBody;
Card.Footer = CardFooter;

export default Card;
